/**
 * Invisible always-focused capture input for the keyboard-wedge scanner
 * (§13.1). The scanner is configured to send the barcode + Enter (CR). Each
 * Enter-terminated line is one scan; we forward the trimmed code and refocus.
 * Keeps focus aggressively so a stray click never silently drops scans.
 */
import { useEffect, useRef } from "react";

export interface ScannerInputProps {
  onScan: (code: string) => void;
  /** Disable capture (e.g. while a modal text field needs the keyboard). */
  disabled?: boolean;
}

export function ScannerInput({ onScan, disabled }: ScannerInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    // Re-grab focus if anything steals it (touch, blur). Cheap interval is
    // robust against the many ways focus can wander on a kiosk — BUT never yank
    // focus away from a real editable control (the manual-weight / mark-short
    // fields), or the operator can't type into them.
    const id = window.setInterval(() => {
      const active = document.activeElement as HTMLElement | null;
      const editing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active != null && active.isContentEditable);
      if (active !== el && !editing) el.focus();
    }, 400);
    return () => window.clearInterval(id);
  }, [disabled]);

  return (
    <input
      ref={ref}
      className="scanner-capture"
      aria-hidden="true"
      tabIndex={-1}
      autoComplete="off"
      spellCheck={false}
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const code = e.currentTarget.value.trim();
        e.currentTarget.value = "";
        if (code) onScan(code);
      }}
    />
  );
}
